import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Labelbox, Table } from "../../components";
import { CLIENT_ACTION, EMPLOYEE_ACTION } from "../../redux/action";
import { Toaster } from "../../utils/common";
import "./client_transfer.scss";

function getClientId(client) {
  return String(client?.client_id ?? client?.id ?? "").trim();
}

function getClientName(client) {
  const personal = `${client?.first_name || ""} ${client?.last_name || ""}`.trim();
  const business = String(
    client?.business_name || client?.businessName || "",
  ).trim();
  return personal || business || "—";
}

const TRANSFER_TABLE_HEADER = [
  { id: "selected", label: "" },
  { id: "name", label: "Name" },
  { id: "email", label: "Email" },
  { id: "client_type", label: "Type" },
];

const ClientTransfer = () => {
  const dispatch = useDispatch();
  const userRole = (localStorage.getItem("user_role") || "").toLowerCase();
  const canUse = userRole === "admin" || userRole === "manager";
  const loggedUserId = localStorage.getItem("user_id");

  const {
    employee: { activeEmployees },
  } = useSelector((s) => s);

  const [fromEmployeeId, setFromEmployeeId] = useState("");
  const [toEmployeeId, setToEmployeeId] = useState("");
  const [assignedClients, setAssignedClients] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const tableRowByIndexRef = useRef([]);

  useEffect(() => {
    if (!canUse) return;
    dispatch(EMPLOYEE_ACTION.getActiveEmployees());
  }, [canUse, dispatch]);

  const allEmployeeOptions = useMemo(() => {
    const list = (activeEmployees || [])
      .map((e) => ({
        id: String(e.employee_id ?? e.id ?? ""),
        value: `${e.first_name || ""} ${e.last_name || ""}`.trim() ||
          String(e.email || e.employee_id || ""),
        email: e.email || "",
      }))
      .filter((x) => x.id);
    return [{ id: "", value: "All" }, ...list];
  }, [activeEmployees, loggedUserId, userRole]);

  const fromEmployeeOptions = useMemo(() => {
    if (userRole !== "manager") return allEmployeeOptions;
    const managerId = String(loggedUserId || "");
    const scoped = (allEmployeeOptions || []).filter((opt) => {
      if (!opt?.id) return true; // keep "Select employee"
      const emp = (activeEmployees || []).find(
        (e) => String(e.employee_id ?? e.id ?? "") === String(opt.id),
      );
      if (!emp) return false;
      const isSelf = String(opt.id) === managerId;
      return !isSelf && String(emp?.manager_id || "") === managerId;
    });
    return scoped;
  }, [allEmployeeOptions, activeEmployees, loggedUserId, userRole]);

  const allClientIds = useMemo(
    () =>
      (assignedClients || [])
        .map(getClientId)
        .filter(Boolean),
    [assignedClients],
  );

  const allSelected =
    allClientIds.length > 0 &&
    allClientIds.every((id) => selectedClientIds.includes(id));

  useEffect(() => {
    const run = async () => {
      if (!fromEmployeeId) {
        setAssignedClients([]);
        setSelectedClientIds([]);
        return;
      }
      setLoadingClients(true);
      setSelectedClientIds([]);
      try {
        const rows = await CLIENT_ACTION.getClientsAssignedToEmployee(
          fromEmployeeId,
        );
        setAssignedClients(Array.isArray(rows) ? rows : []);
      } catch (e) {
        setAssignedClients([]);
        Toaster.error("Failed to load assigned clients");
      } finally {
        setLoadingClients(false);
      }
    };
    if (canUse) run();
  }, [canUse, fromEmployeeId]);

  const tableData = useMemo(() => {
    const rows = (assignedClients || [])
      .map((client) => {
        const id = getClientId(client);
        if (!id) return null;
        return {
          selected: selectedClientIds.includes(id),
          name: getClientName(client),
          email: client.email || "—",
          client_type:
            client.client_type ||
            client.clientType ||
            client.type ||
            "—",
          _clientId: id,
        };
      })
      .filter(Boolean);
    tableRowByIndexRef.current = rows;
    return rows;
  }, [assignedClients, selectedClientIds]);

  const tableFields = useMemo(
    () => [
      {
        type: "checkbox",
        fieldKey: "selected",
        changeData: (checked, _fieldKey, rowIndex) => {
          const id = tableRowByIndexRef.current[rowIndex]?._clientId;
          if (!id) return;
          setSelectedClientIds((prev) => {
            if (checked) return prev.includes(id) ? prev : [...prev, id];
            return prev.filter((x) => x !== id);
          });
        },
      },
      { type: "label", fieldKey: "name" },
      { type: "label", fieldKey: "email" },
      { type: "label", fieldKey: "client_type" },
    ],
    [],
  );

  const handleSelectAll = (checked) => {
    setSelectedClientIds(checked ? [...allClientIds] : []);
  };

  const canSubmit =
    canUse &&
    fromEmployeeId &&
    toEmployeeId &&
    fromEmployeeId !== toEmployeeId &&
    selectedClientIds.length > 0 &&
    !loadingClients &&
    !submitting;

  const onTransfer = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await CLIENT_ACTION.transferClientsToEmployee({
        from_employee_id: fromEmployeeId,
        to_employee_id: toEmployeeId,
        client_ids: selectedClientIds.map(String),
      });
      if (res?.status === 1 || res?.data?.status === 1) {
        Toaster.success(res?.message || res?.data?.message || "Transferred");
        const rows = await CLIENT_ACTION.getClientsAssignedToEmployee(
          fromEmployeeId,
        );
        setAssignedClients(Array.isArray(rows) ? rows : []);
        setSelectedClientIds([]);
      } else {
        Toaster.error(res?.message || res?.data?.message || "Transfer failed");
      }
    } catch (_e) {
      Toaster.error("Transfer failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canUse) {
    return (
      <div className="client-transfer-page">
        <div className="client-transfer-card">
          <div className="client-transfer-subtitle">
            You don't have access to this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-transfer-page">
      <div className="client-transfer-card">
        <div className="client-transfer-header">
          <div>
            <div className="client-transfer-subtitle">
              Transfer assigned clients from one employee to another. Select
              clients in the table below, then transfer to the destination
              employee.
            </div>
          </div>
          <Button
            text={
              submitting
                ? "Transferring..."
                : `Transfer${selectedClientIds.length ? ` (${selectedClientIds.length})` : ""}`
            }
            handleClick={onTransfer}
            disabled={!canSubmit}
            large
          />
        </div>

        <div className="client-transfer-grid">
          <Labelbox
            type="select"
            labelname="From employee"
            dropdown={fromEmployeeOptions}
            value={fromEmployeeId}
            changeData={(v) => {
              setFromEmployeeId(String(v || ""));
              if (String(v || "") === toEmployeeId) setToEmployeeId("");
            }}
          />

          <Labelbox
            type="select"
            labelname="To employee"
            dropdown={allEmployeeOptions.filter(
              (e) => !fromEmployeeId || String(e.id) !== String(fromEmployeeId),
            )}
            value={toEmployeeId}
            changeData={(v) => setToEmployeeId(String(v || ""))}
          />
        </div>

        <div className="client-transfer-clients">
          <div className="client-transfer-clients-header">
            <div className="client-transfer-clients-title">
              Assigned clients
            </div>
            <div className="client-transfer-clients-meta">
              {loadingClients
                ? "Loading…"
                : fromEmployeeId
                  ? `${assignedClients.length} client(s)${
                      selectedClientIds.length
                        ? ` · ${selectedClientIds.length} selected`
                        : ""
                    }`
                  : "Select a From employee"}
            </div>
          </div>

          {!fromEmployeeId ? (
            <p className="client-transfer-table-empty">
              Select a From employee to load their assigned clients.
            </p>
          ) : loadingClients ? (
            <p className="client-transfer-table-empty">Loading clients…</p>
          ) : assignedClients.length === 0 ? (
            <p className="client-transfer-table-empty">
              No clients are assigned to this employee.
            </p>
          ) : (
            <div className="client-transfer-table-scope">
              <div className="client-transfer-select-all">
                <label className="client-transfer-select-all-label">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  <span>Select All</span>
                </label>
              </div>
              <Table
                title=""
                header={TRANSFER_TABLE_HEADER}
                data={tableData}
                fields={tableFields}
                EnableSno
                aligncss="dashboard-table"
                hiddenFields={["_clientId"]}
                initialRowsPerPage={10}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientTransfer;
